import React, { useState, useEffect, useCallback } from 'react';
import { vehiclesApi, creditorsApi, formatApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Car,
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  X,
  Check,
  Warning,
  TrendUp,
  CurrencyDollar,
  DownloadSimple
} from '@phosphor-icons/react';

const statusColors = {
  disponible: 'badge-success',
  rentado: 'badge-info',
  en_taller: 'badge-warning',
  vendido: 'badge-neutral'
};

const statusLabels = {
  disponible: 'Disponible',
  rentado: 'Rentado',
  en_taller: 'En Taller',
  vendido: 'Vendido'
};

const Vehicles = () => {
  const { canManage } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [creditors, setCreditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState({
    brand: '',
    sub_brand: '',
    model: '',
    year: new Date().getFullYear(),
    plates: '',
    purchase_cost: '',
    purchase_date: '',
    creditor_id: '',
    weekly_rent: ''
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [vehiclesRes, creditorsRes] = await Promise.all([
        vehiclesApi.getAll(),
        creditorsApi.getAll()
      ]);
      setVehicles(vehiclesRes.data);
      setCreditors(creditorsRes.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);

    const data = {
      ...formData,
      purchase_cost: parseFloat(formData.purchase_cost),
      weekly_rent: parseFloat(formData.weekly_rent),
      year: parseInt(formData.year),
      creditor_id: formData.creditor_id || null
    };

    try {
      if (editingVehicle) {
        await vehiclesApi.update(editingVehicle.id, data);
      } else {
        await vehiclesApi.create(data);
      }
      setShowModal(false);
      setEditingVehicle(null);
      resetForm();
      loadData();
    } catch (err) {
      setFormError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      brand: '',
      sub_brand: '',
      model: '',
      year: new Date().getFullYear(),
      plates: '',
      purchase_cost: '',
      purchase_date: '',
      creditor_id: '',
      weekly_rent: ''
    });
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      brand: vehicle.brand || '',
      sub_brand: vehicle.sub_brand || '',
      model: vehicle.model || '',
      year: vehicle.year || new Date().getFullYear(),
      plates: vehicle.plates || '',
      purchase_cost: vehicle.purchase_cost || '',
      purchase_date: vehicle.purchase_date || '',
      creditor_id: vehicle.creditor_id || '',
      weekly_rent: vehicle.weekly_rent || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este vehículo?')) return;
    try {
      await vehiclesApi.delete(id);
      loadData();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleStatusChange = async (vehicleId, newStatus) => {
    try {
      await vehiclesApi.update(vehicleId, { status: newStatus });
      setVehicles(vehicles.map(v => v.id === vehicleId ? { ...v, status: newStatus } : v));
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const filteredVehicles = vehicles.filter(
    (v) =>
      v.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.plates?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-[#0F172A]">Vehículos</h1>
          <p className="text-[#64748B]">{vehicles.length} vehículos registrados</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={vehiclesApi.exportExcelUrl(searchTerm ? { q: searchTerm } : {})}
            download
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
            title={searchTerm ? `Descargar filtrado por "${searchTerm}" a Excel` : 'Descargar tabla de vehículos a Excel'}
            data-testid="export-vehicles-excel"
          >
            <DownloadSimple size={16} weight="bold" /> Excel{searchTerm ? ' (filtro)' : ''}
          </a>
          {canManage && (
            <button
              onClick={() => {
                setEditingVehicle(null);
                resetForm();
                setShowModal(true);
              }}
              className="btn-primary flex items-center gap-2"
              data-testid="add-vehicle-btn"
            >
              <Plus size={18} />
              Nuevo Vehículo
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger flex items-center gap-2">
          <Warning size={20} />
          {error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="kpi-card">
          <p className="kpi-label">Total</p>
          <p className="text-2xl font-bold text-[#0F172A]">{vehicles.length}</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Rentados</p>
          <p className="text-2xl font-bold text-[#0055FF]">
            {vehicles.filter((v) => v.status === 'rentado').length}
          </p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Disponibles</p>
          <p className="text-2xl font-bold text-[#10B981]">
            {vehicles.filter((v) => v.status === 'disponible').length}
          </p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">En Taller</p>
          <p className="text-2xl font-bold text-[#F59E0B]">
            {vehicles.filter((v) => v.status === 'en_taller').length}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por marca, modelo o placas..."
          className="input pl-10"
          data-testid="search-vehicles-input"
        />
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="vehicles-table">
            <thead>
              <tr>
                <th>Vehículo</th>
                <th>Placas</th>
                <th>Año</th>
                <th>Costo Compra</th>
                <th>Renta Semanal</th>
                <th>Estatus</th>
                {canManage && <th>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {filteredVehicles.length === 0 ? (
                <tr>
                  <td colSpan={canManage ? 7 : 6} className="text-center py-8 text-[#64748B]">
                    No se encontraron vehículos
                  </td>
                </tr>
              ) : (
                filteredVehicles.map((vehicle) => (
                  <tr key={vehicle.id} data-testid={`vehicle-row-${vehicle.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#F1F5F9] rounded-lg flex items-center justify-center">
                          <Car size={20} className="text-[#64748B]" />
                        </div>
                        <div>
                          <p className="font-medium">{vehicle.brand} {vehicle.sub_brand}</p>
                          <p className="text-sm text-[#64748B]">{vehicle.model}</p>
                        </div>
                      </div>
                    </td>
                    <td className="font-mono font-medium">{vehicle.plates}</td>
                    <td>{vehicle.year}</td>
                    <td>{formatCurrency(vehicle.purchase_cost)}</td>
                    <td className="font-medium text-[#10B981]">{formatCurrency(vehicle.weekly_rent)}</td>
                    <td>
                      <select
                        value={vehicle.status || 'disponible'}
                        onChange={(e) => handleStatusChange(vehicle.id, e.target.value)}
                        className={`text-xs font-medium rounded-full px-2 py-1 border-0 cursor-pointer ${statusColors[vehicle.status] || 'badge-neutral'}`}
                        data-testid={`status-vehicle-${vehicle.id}`}
                      >
                        {Object.entries(statusLabels).map(([val, label]) => (
                          <option key={val} value={val}>{label}</option>
                        ))}
                      </select>
                    </td>
                    {canManage && (
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(vehicle)}
                            className="p-2 text-[#64748B] hover:text-[#0055FF] hover:bg-blue-50 rounded"
                            data-testid={`edit-vehicle-${vehicle.id}`}
                          >
                            <PencilSimple size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(vehicle.id)}
                            className="p-2 text-[#64748B] hover:text-[#E11D48] hover:bg-red-50 rounded"
                            data-testid={`delete-vehicle-${vehicle.id}`}
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" data-testid="vehicle-modal">
          <div className="modal-content animate-fade-in max-w-2xl">
            <div className="p-6 border-b border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-heading font-bold">
                  {editingVehicle ? 'Editar Vehículo' : 'Nuevo Vehículo'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="alert alert-danger text-sm">{formError}</div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="label">Marca *</label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    className="input"
                    required
                    placeholder="Nissan"
                    data-testid="vehicle-brand-input"
                  />
                </div>
                <div>
                  <label className="label">Submarca *</label>
                  <input
                    type="text"
                    value={formData.sub_brand}
                    onChange={(e) => setFormData({ ...formData, sub_brand: e.target.value })}
                    className="input"
                    required
                    placeholder="Versa"
                    data-testid="vehicle-subbrand-input"
                  />
                </div>
                <div>
                  <label className="label">Modelo *</label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="input"
                    required
                    placeholder="Sense"
                    data-testid="vehicle-model-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Año *</label>
                  <input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    className="input"
                    required
                    min="2000"
                    max="2030"
                    data-testid="vehicle-year-input"
                  />
                </div>
                <div>
                  <label className="label">Placas *</label>
                  <input
                    type="text"
                    value={formData.plates}
                    onChange={(e) => setFormData({ ...formData, plates: e.target.value.toUpperCase() })}
                    className="input font-mono"
                    required
                    placeholder="ABC-123-A"
                    data-testid="vehicle-plates-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Costo de Compra *</label>
                  <input
                    type="number"
                    value={formData.purchase_cost}
                    onChange={(e) => setFormData({ ...formData, purchase_cost: e.target.value })}
                    className="input"
                    required
                    min="0"
                    step="0.01"
                    placeholder="250000"
                    data-testid="vehicle-cost-input"
                  />
                </div>
                <div>
                  <label className="label">Fecha de Compra *</label>
                  <input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    className="input"
                    required
                    data-testid="vehicle-date-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Renta Semanal *</label>
                  <input
                    type="number"
                    value={formData.weekly_rent}
                    onChange={(e) => setFormData({ ...formData, weekly_rent: e.target.value })}
                    className="input"
                    required
                    min="0"
                    step="0.01"
                    placeholder="2500"
                    data-testid="vehicle-rent-input"
                  />
                </div>
                <div>
                  <label className="label">Acreedor (opcional)</label>
                  <select
                    value={formData.creditor_id}
                    onChange={(e) => setFormData({ ...formData, creditor_id: e.target.value })}
                    className="input"
                    data-testid="vehicle-creditor-select"
                  >
                    <option value="">Sin acreedor</option>
                    {creditors.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                  data-testid="save-vehicle-btn"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Check size={18} />
                  )}
                  {editingVehicle ? 'Guardar Cambios' : 'Crear Vehículo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehicles;
